/**
 * brief-helpers — pure helpers shared by the per-pillar brief composers
 * (`compliance-brief.ts`, `security-brief.ts`, `identity-brief.ts`).
 *
 * These four functions were duplicated byte-for-byte across the three
 * composers. The pillar-specific bits (verdict tables, modifier bands,
 * imperative-verb defaults, number-word arrays) intentionally stay co-located
 * with each composer so the editorial voice for one pillar can shift without
 * pulling on the others. Only the truly mechanical helpers live here.
 *
 * `posture-brief.ts` (the original Command Center composer) carries its own
 * private copies of these helpers — left untouched so its 32 vitest cases keep
 * exercising a fully self-contained module.
 */

/** Format today as "Today's Brief · 29 April" using en-GB long-month. */
export function formatKicker(now: Date): string {
  const fmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' })
  return `Today's Brief · ${fmt.format(now)}`
}

/** Local-day key (YYYY-MM-DD) — matches the kicker's local-time framing. */
export function localDayKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** djb2 string hash. Stable across runs, no crypto needed for variant picking. */
export function hash(s: string): number {
  let h = 5381
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i)
  }
  return h >>> 0
}

/** Pick one variant deterministically from `(seed, slot)`. */
export function pickVariant<T>(variants: readonly T[], seed: string, slot: string): T {
  if (variants.length === 0) throw new Error(`pickVariant: empty variants for slot "${slot}"`)
  const idx = hash(`${seed}|${slot}`) % variants.length
  return variants[idx]
}

/** Capitalize the first character of a non-empty string. */
export function cap(s: string): string {
  if (!s) return s
  return s[0].toUpperCase() + s.slice(1)
}

/**
 * Buckets a 30-day score delta into a five-state direction so every brief slot
 * (modifier, verdict, weak-link, deck-trend, deck-action) routes off the same
 * signal. The boundaries match what the existing `TREND_*` pools in each
 * composer already use — surfacing it as one helper just stops the modifier
 * and verdict tables from re-implementing the same gates with stale logic.
 *
 *   `risingFast`  delta ≥ +3      — TREND_LIFTING_FAST
 *   `rising`      0 < delta < +3  — TREND_LIFTING
 *   `flat`        delta === 0     — TREND_FLAT
 *   `slipping`    -3 ≤ delta < 0  — TREND_SLIPPING
 *   `falling`     delta < -3      — TREND_FALLING
 *
 * Coherence rule: any slot that names motion (modifier "weakening", verdict
 * "settled", action "hold the cadence") must consult this bucket — never just
 * the score band — or it will contradict the trend sentence which already
 * does.
 */
export type DeltaBucket = 'risingFast' | 'rising' | 'flat' | 'slipping' | 'falling'

export function bucketDelta(deltaValue: number): DeltaBucket {
  if (deltaValue >= 3) return 'risingFast'
  if (deltaValue > 0) return 'rising'
  if (deltaValue === 0) return 'flat'
  if (deltaValue >= -3) return 'slipping'
  return 'falling'
}

/** True for any non-negative direction (rising, risingFast, flat). */
export function isNonNegative(bucket: DeltaBucket): boolean {
  return bucket === 'rising' || bucket === 'risingFast' || bucket === 'flat'
}

/** True when posture is actually declining (slipping or falling). */
export function isNegative(bucket: DeltaBucket): boolean {
  return bucket === 'slipping' || bucket === 'falling'
}

/**
 * Format an ISO timestamp as a relative-time phrase for the editorial byline
 * row, e.g. "Updated 12 minutes ago" or "Updated just now".
 *
 * Tone: lead with the verb ("Updated"), not the system. Mirrors the existing
 * Command Center / Operations panel sync-age framing while staying inside the
 * advisor voice (CLAUDE.md Product Voice rule #4: byline carries provenance +
 * scope; no naked "Last sync: 2026-05-12T14:23:00Z" chips).
 *
 * Inputs and returns:
 *   - `null`, `undefined`, empty string, or unparseable string → `null`.
 *     Callers should render a "never synced" copy line of their own choosing
 *     (e.g. "Awaiting first sync.") so we don't bake the grace copy into a
 *     pure formatter.
 *   - Otherwise returns a string starting with "Updated".
 *
 * Pure: returns a fresh string each call so the formatter is safe for unit
 * tests. Callers that need "now" injected for testing can pass `asOf`.
 */
export function formatRelativeTime(
  timestamp: string | null | undefined,
  asOf: Date = new Date(),
): string | null {
  if (!timestamp) return null
  const t = new Date(timestamp).getTime()
  if (Number.isNaN(t)) return null

  const ageMs = Math.max(0, asOf.getTime() - t)
  const minute = 60_000
  const hour = 60 * minute
  const day = 24 * hour
  const week = 7 * day

  if (ageMs < minute) return 'Updated just now'

  if (ageMs < hour) {
    const m = Math.floor(ageMs / minute)
    return `Updated ${m} minute${m === 1 ? '' : 's'} ago`
  }

  if (ageMs < day) {
    const h = Math.floor(ageMs / hour)
    return `Updated ${h} hour${h === 1 ? '' : 's'} ago`
  }

  if (ageMs < week) {
    const d = Math.floor(ageMs / day)
    return `Updated ${d} day${d === 1 ? '' : 's'} ago`
  }

  // Beyond a week, fall back to a calendar phrasing so the editorial voice
  // doesn't strain into "Updated 47 days ago". Reads like a wire byline.
  const fmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' })
  return `Updated ${fmt.format(new Date(t))}`
}
